import { useEffect, useState } from "react";
import { Plus, Calendar as CalIcon, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const categoryColors = {
  general: "#78716C",
  medico: "#B5838D",
  scuola: "#81B29A",
  scadenza: "#F2CC8F",
  compleanno: "#E07A5F",
  appuntamento: "#3D5A80",
};

const formatDate = (d) => new Date(d).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "long" });

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", time: "", description: "", category: "general" });

  const refresh = async () => {
    const { data } = await api.get("/calendar");
    setEvents(data);
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/calendar", form);
    toast.success("Evento aggiunto");
    setForm({ title: "", date: "", time: "", description: "", category: "general" });
    setShowForm(false);
    refresh();
  };

  const remove = async (id) => {
    await api.delete(`/calendar/${id}`);
    refresh();
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.date >= today);
  const past = events.filter(e => e.date < today);

  return (
    <div className="px-5 md:px-10 py-8 max-w-3xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold">Famiglia</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <CalIcon className="w-7 h-7 text-info" /> Agenda
          </h1>
        </div>
        <button data-testid="calendar-add-toggle" onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Evento
        </button>
      </div>

      <div className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold mb-3">Prossimi ({upcoming.length})</div>
      <div className="space-y-2">
        {upcoming.map(ev => (
          <div key={ev.id} className="bg-surface border border-subtle rounded-2xl p-4 flex items-start gap-3 animate-fade-up">
            <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: categoryColors[ev.category] || "#78716C" }} />
            <div className="flex-1">
              <div className="text-xs font-mono text-brand">{formatDate(ev.date)}{ev.time && <span className="text-fg font-semibold"> · ore {ev.time}</span>}</div>
              <div className="font-medium mt-0.5">{ev.title}</div>
              {ev.description && <div className="text-xs text-tertiary-fg mt-1">{ev.description}</div>}
              <div className="text-[10px] text-tertiary-fg mt-2 uppercase tracking-widest">{ev.category} · {ev.created_by}</div>
            </div>
            <button onClick={() => remove(ev.id)} className="p-2 rounded-full hover:bg-elevated text-rose-warm">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {upcoming.length === 0 && <div className="text-center text-tertiary-fg py-8 text-sm">Nessun evento futuro.</div>}
      </div>

      {past.length > 0 && (
        <>
          <div className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold mb-3 mt-8">Passati ({past.length})</div>
          <div className="space-y-2 opacity-60">
            {past.slice(0, 10).map(ev => (
              <div key={ev.id} className="bg-surface/50 border border-subtle rounded-2xl p-3 text-sm">
                <span className="text-tertiary-fg">{formatDate(ev.date)}{ev.time ? ` · ore ${ev.time}` : ""} · </span>{ev.title}
              </div>
            ))}
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-app/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form
            onSubmit={submit}
            className="bg-surface border border-subtle rounded-3xl max-w-md w-full p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-semibold">Nuovo evento</h3>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-elevated"><X className="w-4 h-4" /></button>
            </div>
            <input data-testid="calendar-event-title" required className="input-warm" placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="calendar-event-date" required type="date" className="input-warm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <input data-testid="calendar-event-time" type="time" className="input-warm" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>
            <p className="text-[11px] text-tertiary-fg -mt-1">Orario opzionale — utile per appuntamenti (es. parrucchiere ore 15:30)</p>
            <textarea data-testid="calendar-event-desc" rows={3} className="input-warm" placeholder="Descrizione (opzionale)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <select data-testid="calendar-event-category" className="input-warm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="general">Generale</option>
              <option value="medico">Medico</option>
              <option value="scuola">Scuola</option>
              <option value="scadenza">Scadenza</option>
              <option value="compleanno">Compleanno</option>
              <option value="appuntamento">Appuntamento</option>
            </select>
            <button data-testid="calendar-event-submit" type="submit" className="btn-primary w-full">Crea evento</button>
          </form>
        </div>
      )}
    </div>
  );
}
